import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './JsonFormatter.module.css';

const indentText = (level: number) => '  '.repeat(level);
const rootPath = '$';
const RESERVED_BOTTOM_SPACE = 180;
const MIN_EDITOR_HEIGHT = 140;

const formatJsonDisplay = (value: unknown, level: number, collapsedArrayPaths: Set<string>, path: string): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';

    if (collapsedArrayPaths.has(path)) {
      const inlineItems = value.map((item) => formatJsonDisplay(item, 0, collapsedArrayPaths, `${path}[*]`)).join(', ');
      return `[/* ${value.length} items */ ${inlineItems}]`;
    }

    const lines = value.map((item, index) =>
      `${indentText(level + 1)}${formatJsonDisplay(item, level + 1, collapsedArrayPaths, `${path}[${index}]`)}`
    );
    return `[\n${lines.join(',\n')}\n${indentText(level)}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return '{}';

  const lines = entries.map(
    ([key, val]) =>
      `${indentText(level + 1)}${JSON.stringify(key)}: ${formatJsonDisplay(
        val,
        level + 1,
        collapsedArrayPaths,
        `${path}.${key}`
      )}`
  );
  return `{\n${lines.join(',\n')}\n${indentText(level)}}`;
};

const JsonFormatter: React.FC = () => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [parsedJson, setParsedJson] = useState<unknown | null>(null);
  const [isViewerMode, setIsViewerMode] = useState(false);
  const [collapsedArrayPaths, setCollapsedArrayPaths] = useState<Set<string>>(new Set());
  const [editorHeight, setEditorHeight] = useState<number>(220);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const showMessage = (text: string, isError = false) => {
    setErrorMessage(isError ? text : '');
    setMessage(!isError ? text : '');
    setTimeout(() => {
      setMessage('');
      setErrorMessage('');
    }, 2500);
  };

  const refreshFormattedText = (value: unknown, nextCollapsedPaths: Set<string>) => {
    setText(formatJsonDisplay(value, 0, nextCollapsedPaths, rootPath));
  };

  const handleFormat = () => {
    if (!text.trim()) {
      showMessage(t('jsonFormatter.tipEmptyJson'), true);
      return;
    }

    try {
      const parsed = JSON.parse(text);
      setParsedJson(parsed);
      setIsViewerMode(true);
      const emptyCollapsedSet = new Set<string>();
      setCollapsedArrayPaths(emptyCollapsedSet);
      refreshFormattedText(parsed, emptyCollapsedSet);
    } catch {
      showMessage(t('jsonFormatter.tipInvalidJson'), true);
    }
  };

  const handleToggleArray = (path: string) => {
    if (!parsedJson) return;
    const next = new Set(collapsedArrayPaths);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
    }
    setCollapsedArrayPaths(next);
    refreshFormattedText(parsedJson, next);
  };

  const handleEditMode = () => {
    setIsViewerMode(false);
  };

  const handleCopy = async () => {
    if (!text) {
      showMessage(t('jsonFormatter.tipNoCopyContent'), true);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showMessage(t('jsonFormatter.tipCopySuccess'));
    } catch {
      showMessage(t('jsonFormatter.tipCopyFailed'), true);
    }
  };

  const renderNode = (value: unknown, level: number, path: string, keyName?: string, isLast = true): React.ReactNode => {
    const indent = indentText(level);
    const suffix = isLast ? '' : ',';

    if (Array.isArray(value)) {
      const isCollapsed = collapsedArrayPaths.has(path);
      const labelPrefix = keyName ? `${JSON.stringify(keyName)}: ` : '';

      if (isCollapsed) {
        return (
          <div className={styles.jsonLine}>
            <span className={styles.indent}>{indent}</span>
            <button
              className={styles.toggleIcon}
              onClick={() => handleToggleArray(path)}
              aria-label={t('jsonFormatter.ariaExpandArray')}
              type="button"
            >
              ▶
            </button>
            <span>
              {labelPrefix}[... {value.length} items]{suffix}
            </span>
          </div>
        );
      }

      const children = value.map((item, index) => (
        <React.Fragment key={`${path}[${index}]`}>
          {renderNode(item, level + 1, `${path}[${index}]`, undefined, index === value.length - 1)}
        </React.Fragment>
      ));

      return (
        <>
          <div className={styles.jsonLine}>
            <span className={styles.indent}>{indent}</span>
            <button
              className={styles.toggleIcon}
              onClick={() => handleToggleArray(path)}
              aria-label={t('jsonFormatter.ariaCollapseArray')}
              type="button"
            >
              ▼
            </button>
            <span>{labelPrefix}[</span>
          </div>
          {children}
          <div className={styles.jsonLine}>
            <span className={styles.indent}>{indent}</span>
            <span>]{suffix}</span>
          </div>
        </>
      );
    }

    if (value && typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>);
      const labelPrefix = keyName ? `${JSON.stringify(keyName)}: ` : '';

      return (
        <>
          <div className={styles.jsonLine}>
            <span className={styles.indent}>{indent}</span>
            <span>{labelPrefix}{'{'}</span>
          </div>
          {entries.map(([key, val], index) => (
            <React.Fragment key={`${path}.${key}`}>
              {renderNode(val, level + 1, `${path}.${key}`, key, index === entries.length - 1)}
            </React.Fragment>
          ))}
          <div className={styles.jsonLine}>
            <span className={styles.indent}>{indent}</span>
            <span>{'}'}{suffix}</span>
          </div>
        </>
      );
    }

    return (
      <div className={styles.jsonLine}>
        <span className={styles.indent}>{indent}</span>
        <span>
          {keyName ? `${JSON.stringify(keyName)}: ` : ''}
          {JSON.stringify(value)}
          {suffix}
        </span>
      </div>
    );
  };

  const handleClear = () => {
    setText('');
    setParsedJson(null);
    setIsViewerMode(false);
    setCollapsedArrayPaths(new Set());
    setMessage('');
    setErrorMessage('');
  };

  useEffect(() => {
    if (isViewerMode) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    const updateHeight = () => {
      const rect = textarea.getBoundingClientRect();
      const maxHeight = Math.max(MIN_EDITOR_HEIGHT, window.innerHeight - rect.top - RESERVED_BOTTOM_SPACE);

      textarea.style.height = 'auto';
      const contentHeight = textarea.scrollHeight;
      const nextHeight = Math.max(MIN_EDITOR_HEIGHT, Math.min(contentHeight, maxHeight));
      setEditorHeight(nextHeight);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [text, isViewerMode]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('jsonFormatter.title')}</h2>
        <p className={styles.description}>{t('jsonFormatter.description')}</p>

        {(message || errorMessage) && (
          <div className={`${styles.tip} ${errorMessage ? styles.error : styles.success}`}>
            {errorMessage || message}
          </div>
        )}

        <div>
          <div className={styles.label}>{t('jsonFormatter.labelJsonContent')}</div>
          {isViewerMode && parsedJson !== null ? (
            <div className={styles.singleBox}>
              {renderNode(parsedJson, 0, rootPath)}
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={styles.textarea}
              style={{ height: `${editorHeight}px` }}
              placeholder={t('jsonFormatter.placeholderExample')}
            />
          )}
        </div>

        <div className={styles.actions}>
          <button onClick={handleFormat} className={styles.primaryButton}>
            {t('jsonFormatter.btnFormat')}
          </button>
          <button onClick={handleCopy} className={styles.secondaryButton}>
            {t('jsonFormatter.btnCopy')}
          </button>
          {isViewerMode && (
            <button onClick={handleEditMode} className={styles.secondaryButton}>
              {t('jsonFormatter.btnBackToEdit')}
            </button>
          )}
          <button onClick={handleClear} className={styles.secondaryButton}>
            {t('jsonFormatter.btnClear')}
          </button>
        </div>

        <div className={styles.footer}>{t('jsonFormatter.footerTip')}</div>
      </div>
    </div>
  );
};

export default JsonFormatter;
